import React, { useCallback, useEffect, useRef, useState } from 'react';

interface HorizontalVirtualListProps<T = unknown> {
  items: T[];
  hasNext?: boolean;
  isFetchingNext?: boolean;
  onReachEnd?: () => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
  renderShowAll?: () => React.ReactNode;
  className?: string;
  itemGapPx?: number;
  scrollableTarget?: string;
  itemWidth?: number | string; // Width of each item in pixels or percentage (e.g., "30%")
  bufferSize?: number; // Number of items to render outside viewport
}

const HorizontalVirtualList = <T,>({
  items,
  hasNext = false,
  isFetchingNext = false,
  onReachEnd,
  renderItem,
  renderLoading,
  renderShowAll,
  className = '',
  itemGapPx = 8,
  scrollableTarget,
  itemWidth = 120, // Default width for 30% of container
  bufferSize = 2, // Render 2 extra items on each side
}: HorizontalVirtualListProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 });
  const [containerWidth, setContainerWidth] = useState(0);
  const [actualItemWidth, setActualItemWidth] = useState(120);

  // Calculate actual item width based on container width and itemWidth prop
  const calculateItemWidth = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    setContainerWidth(width);

    if (typeof itemWidth === 'string' && itemWidth.includes('%')) {
      // Parse percentage and calculate pixel value
      const percentage = parseFloat(itemWidth.replace('%', '')) / 100;
      const calculatedWidth = width * percentage;
      setActualItemWidth(calculatedWidth);
    } else {
      // Use pixel value directly
      setActualItemWidth(typeof itemWidth === 'number' ? itemWidth : 120);
    }
  }, [itemWidth]);

  const itemPitch = actualItemWidth + itemGapPx;

  // Calculate visible range based on scroll position
  const calculateVisibleRange = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (itemPitch <= 0) return;

    const scrollLeft = container.scrollLeft;
    const clientWidth = container.clientWidth;

    const startIndex = Math.max(
      0,
      Math.floor(scrollLeft / itemPitch) - bufferSize,
    );
    const endIndex = Math.min(
      items.length - 1,
      Math.ceil((scrollLeft + clientWidth) / itemPitch) + bufferSize,
    );

    setVisibleRange({ start: startIndex, end: endIndex });
  }, [bufferSize, items.length, itemPitch]);

  // Update visible range when scroll position changes
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const { scrollLeft, scrollWidth, clientWidth } = target;

      // Update visible range
      calculateVisibleRange();

      // Check if we need to load more (when scrolled to 80% of content)
      if (hasNext && onReachEnd && !isFetchingNext) {
        const scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;
        if (scrollPercentage > 0.8) {
          onReachEnd();
        }
      }
    },
    [hasNext, onReachEnd, isFetchingNext, calculateVisibleRange],
  );

  // Initialize visible range and container width
  useEffect(() => {
    calculateItemWidth();
  }, [calculateItemWidth]);

  // Update visible range when actualItemWidth changes
  useEffect(() => {
    if (!containerRef.current) return;
    if (itemPitch <= 0) return;

    const itemsPerView = Math.ceil(containerWidth / itemPitch);
    const endIndex = Math.min(items.length - 1, itemsPerView + bufferSize);
    setVisibleRange({ start: 0, end: endIndex });
  }, [containerWidth, itemPitch, bufferSize, items.length]);

  // Update visible range when items change
  useEffect(() => {
    calculateVisibleRange();
  }, [items.length, calculateVisibleRange]);

  // Get visible items
  const visibleItems = items.slice(visibleRange.start, visibleRange.end + 1);

  return (
    <div
      ref={containerRef}
      className={`flex py-4 overflow-x-auto overscroll-x-contain ${className}`}
      style={{ gap: `${itemGapPx}px` }}
      id={scrollableTarget}
      data-horizontal-scroll
      onScroll={handleScroll}
    >
      {/* Spacer for items before visible range */}
      {visibleRange.start > 0 && (
        <div
          style={{ width: visibleRange.start * itemPitch }}
          className="flex-shrink-0"
        />
      )}

      {/* Render visible items */}
      {visibleItems.map((item, index) => (
        <div
          key={visibleRange.start + index}
          className="flex-shrink-0"
          style={{ width: actualItemWidth }}
        >
          {renderItem(item, visibleRange.start + index)}
        </div>
      ))}

      {/* Loading indicator */}
      {isFetchingNext && renderLoading && (
        <div className="flex-shrink-0" style={{ width: actualItemWidth }}>
          {renderLoading()}
        </div>
      )}

      {/* Show All button */}
      {!hasNext && renderShowAll && (
        <div className="flex-shrink-0" style={{ width: actualItemWidth }}>
          {renderShowAll()}
        </div>
      )}

      {/* Spacer for items after visible range */}
      {visibleRange.end < items.length - 1 && (
        <div
          style={{ width: (items.length - visibleRange.end - 1) * itemPitch }}
          className="flex-shrink-0"
        />
      )}
    </div>
  );
};

export default HorizontalVirtualList;
